import type { OperationNodeSpec, OperationTypeDefinition, Archetype, Duration } from '@/graph/domain/types';
import type { AddStepFormState } from './types';

function isFilledValue(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number') return true;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if ('amount' in obj) {
      const amount = obj.amount;
      return amount != null && String(amount).trim().length > 0;
    }
    if ('value' in obj) {
      const inner = obj.value;
      if (inner == null) return false;
      if (typeof inner === 'string') return inner.trim().length > 0;
      return true;
    }
    return Object.keys(obj).length > 0;
  }
  return true;
}

/**
 * Unwrap parameter wrapper format recursively.
 * Removes { parameterId, parameterName, value } wrapper layers to get actual value.
 */
function unwrapParameterWrapper(value: unknown, depth = 0): unknown {
  if (depth > 10) return value; // Prevent infinite recursion
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
  
  const obj = value as Record<string, unknown>;
  // If it looks like a parameter wrapper (has parameterId/parameterName and value field)
  if (('parameterId' in obj || 'parameterName' in obj) && 'value' in obj && obj.value !== undefined) {
    // Recurse into the value field
    return unwrapParameterWrapper(obj.value, depth + 1);
  }
  
  return value;
}

export function formToOperationSpec(
  form: AddStepFormState,
  operationType: OperationTypeDefinition,
): OperationNodeSpec {
  const duration: Duration | undefined =
    operationType.durationAllow
      ? {
          isInfinity: false,
          value: form.durationHours * 3600 + form.durationMinutes * 60 + form.durationSeconds,
          linkToPreviousStep: form.linkToPreviousStep,
          prevStep: form.linkedStepId ?? undefined,
        }
      : undefined;

  const parameters = operationType.parameters.reduce<Record<string, unknown>>((acc, param) => {
    let rawValue =
      form.parameterValues[param.parameterName] ??
      form.parameterValues[param.id] ??
      (param.safeKey ? form.parameterValues[param.safeKey] : undefined);

    if (!isFilledValue(rawValue)) {
      return acc;
    }

    // Unwrap any nested parameter wrapper format
    rawValue = unwrapParameterWrapper(rawValue);

    if (
      (param.fieldType === 'units' || param.fieldType === 'textNumber') &&
      (param.parameterTags?.length ?? 0) > 0
    ) {
      const tagged = (rawValue && typeof rawValue === 'object' ? rawValue : {}) as Record<string, unknown>;
      const amount = tagged.amount ?? tagged.value ?? rawValue;
     const unit =
  typeof tagged.unit === 'string'
    ? tagged.unit
    : typeof tagged.units === 'string'
      ? tagged.units
      : '';

      acc[param.parameterName] = {
        parameterId: param.id,
        parameterName: param.parameterName,
        amount,
        unit,
        units: unit,
      };
      return acc;
    }

    acc[param.parameterName] = {
      parameterId: param.id,
      parameterName: param.parameterName,
      value: rawValue,
    };

    return acc;
  }, {});

  return {
    archetype: operationType.operationType as Archetype,
    operationTypeId: operationType.id,
    displayName: operationType.operationName,
    parameters,
    checkPoints: form.checkpoints.length > 0 ? form.checkpoints : operationType.checkpoints,
    duration,
    recommendedInstrument: form.recommendedInstrument.length > 0 ? form.recommendedInstrument : undefined,
    notes: form.notes || undefined,
    approvers: form.approvers,
    notifications: form.notifications,
    yield: form.yieldValue != null
      ? { amount: form.yieldValue, isPercent: form.yieldIsPercent }
      : undefined,
  };
}
